/**
 * The drawing tool wheel's face: a ring of rounded glass slices around a round
 * hub, opened at the pointer. Presentational only; `ToolWheelHost` owns the
 * gesture. (Chris, 2026-10-08: option C, rounded corners, identical slices,
 * icons only, glass.)
 *
 * Every slice is the same piece: same shape (`ringSlice`), same constant-width
 * gap to its neighbours, the same surface, its icon at the same radius. The
 * surface is the chart dropdowns' popover color made translucent over a heavy
 * blur, with a barely-there radial gradient and a faint light catch on every edge, so
 * the ring reads as glass over the chart while keeping the menus' color. The
 * light is the same all the way round: nothing is lit from the top, so the
 * bottom never reads darker. Focus never changes a slice's size or shape: its
 * fill brightens a little, and it gets a slight accent glow around it, a
 * brighter outline and an accent icon. The glow is the slice's alone; the icon
 * does not grow or glow (Chris, 2026-10-08).
 *
 * Icons only: a slice's name is its tooltip, and the hub shows the full name
 * of the slot in focus. A slot may hold a drawing-bar control instead of a
 * tool (`toolWheelControls`): its glyph and name follow the surface's state,
 * and a control that is on carries the same dot as the armed tool.
 *
 * Opening and closing fade every piece on its own, never through a wrapper:
 * a wrapper below full opacity cuts the glass off from the chart behind it,
 * so the slices showed sharp candles until the fade ended and the blur
 * snapped on (Chris, 2026-10-10). The ring's pop is a scale only.
 *
 * Each slot is two layers. The visible slice is inert; above all slices sits
 * an invisible button clipped to the slot's whole WEDGE, so a click anywhere
 * in that direction lands on it, matching how the held gesture picks by
 * direction. The hub renders last, above the wedges.
 */
import { useId, useMemo } from 'react'
import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react'
import { overlayLabel } from '../utils/overlays'
import { WHEEL_HUB, WHEEL_LAYOUT, ringSlice, slotAngle } from '../utils/toolWheelGeometry'
import { ControlIcons } from './drawingBarChrome'
import { ToolIcons } from './toolIcons'
import { wheelControl, type WheelControls } from './toolWheelControls'
import { WHEEL_EXIT_MS, type WheelState } from './toolWheelState'
import { Z_MODAL } from './zLayers'

export interface ToolWheelProps {
  slots: readonly string[]
  state: WheelState
  /** Closed and fading out: inert, and every click passes through to the chart. */
  exiting?: boolean
  /** The surface's armed tool, marked with a dot. */
  activeTool: string | null
  /** The surface's drawing-bar controls, for control slots' state and handlers. */
  controls?: WheelControls | null
  /** Slot whose swap menu is open; it stays lit while the menu is up. */
  menuSlot: number | null
  /** Whether the layout differs from the defaults (enables Reset). */
  canReset: boolean
  onTileClick: (slot: number, e: ReactMouseEvent) => void
  onTileContextMenu: (slot: number, e: ReactMouseEvent) => void
  onToggleEdit: () => void
  onReset: () => void
}

const SIZE = WHEEL_LAYOUT.size
const C = SIZE / 2
const HUB_R = WHEEL_HUB / 2
/** The dim behind the ring, so the chart steps back during the gesture. */
const SCRIM = SIZE * 1.5
/** Opening fade and pop (ms); closing takes `WHEEL_EXIT_MS`. */
const ENTER_MS = 140
/** Every piece that fades, each on its own (see the header). */
const FADE = 'edge-tool-wheel-fade'

/**
 * The glass: the dropdowns' popover color, translucent, over a heavy blur,
 * with a very soft radial gradient (no sheen) that is the same for every slice.
 */
const GLASS = 'backdrop-blur-xl backdrop-saturate-150'
// White in every theme: glass highlights are light, and the text color is
// dark in light mode, where it drew grey bands across the top slices.
// No sheen: only a very soft radial gradient from the hub, the same for every
// slice and barely lighter toward the rim (Chris, 2026-10-08: no sheen, a
// slight gradient is fine, much softer).
const SHEEN =
  'bg-[radial-gradient(circle_closest-side,transparent_50%,rgb(255_255_255/0.025)_100%)]'
const SLICE_FILL = 'bg-popover/65'
/** The slice in focus: the same surface, lifted 12% toward the text color, so it reads a little brighter. */
const SLICE_FOCUS_FILL =
  'bg-[color-mix(in_srgb,hsl(var(--chart-text))_12%,hsl(var(--popover)/0.65))]'

interface SlotView {
  /** What a pick does: the tool's name, or the control's current action. */
  label: string
  icon: ReactNode
  /** A control that is on (the armed tool is marked separately). */
  on: boolean
  /** False for a control this surface does not offer. */
  available: boolean
}

function slotView(name: string, controls: WheelControls | null | undefined): SlotView {
  const control = wheelControl(name)
  if (!control) {
    return { label: overlayLabel(name), icon: ToolIcons[name], on: false, available: true }
  }
  const c = controls ?? {}
  const on = control.on(c)
  return {
    label: control.name(c),
    icon: ControlIcons[control.icon(on)],
    on,
    available: control.available(c),
  }
}

const wrenchIcon = (
  <svg
    width={14}
    height={14}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </svg>
)

const resetIcon = (
  <svg
    width={14}
    height={14}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
  </svg>
)

function HubButton({
  label,
  title,
  active,
  quiet,
  disabled,
  onClick,
  children,
}: {
  label: string
  title: string
  active?: boolean
  /** A faint glyph, for the wrench while the wheel is only held. */
  quiet?: boolean
  disabled?: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      tabIndex={-1}
      aria-label={label}
      aria-pressed={active}
      title={title}
      disabled={disabled}
      onClick={onClick}
      // Never take focus from the page: the wheel is a pointer gesture.
      onMouseDown={(e) => e.preventDefault()}
      className={
        'flex h-6 w-6 items-center justify-center rounded-full chrome-press ' +
        (active
          ? 'bg-primary text-primary-foreground'
          : quiet
            ? 'text-[hsl(var(--chart-text)/0.38)] hover:text-[hsl(var(--chart-text)/0.85)]'
            : 'text-[hsl(var(--chart-text)/0.72)] hover:bg-accent hover:text-[hsl(var(--chart-text)/0.9)] disabled:pointer-events-none disabled:opacity-40')
      }
    >
      {children}
    </button>
  )
}

export function ToolWheel({
  slots,
  state,
  exiting = false,
  activeTool,
  controls,
  menuSlot,
  canReset,
  onTileClick,
  onTileContextMenu,
  onToggleEdit,
  onReset,
}: ToolWheelProps) {
  const editing = state.phase === 'editing'
  const n = slots.length
  const focus = menuSlot ?? state.hovered
  const views = slots.map((name) => slotView(name, controls))
  const hovered = state.hovered !== null ? (views[state.hovered] ?? null) : null
  const slices = useMemo(() => Array.from({ length: n }, (_, i) => ringSlice(i, n, C)), [n])
  const ids = useId().replace(/:/g, '')
  const glowId = `edge-tool-wheel-glow-${ids}`
  const { ring, slotRadius, cellW, cellH } = WHEEL_LAYOUT

  return (
    <div
      role="menu"
      aria-label="Drawing tool wheel"
      aria-hidden={exiting || undefined}
      inert={exiting}
      data-tool-wheel=""
      data-chart-popup=""
      data-wheel-exiting={exiting ? '' : undefined}
      className="fixed select-none"
      style={{
        left: state.origin.x,
        top: state.origin.y,
        width: SIZE,
        height: SIZE,
        marginLeft: -C,
        marginTop: -C,
        transform: `scale(${state.scale})`,
        zIndex: Z_MODAL,
        pointerEvents: exiting ? 'none' : undefined,
      }}
      // A right-click on the wheel must never open the browser menu, or reach
      // a chart context menu.
      onContextMenu={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      {/* Theme-aware pieces. The dim is the chart's own background, so it
          darkens a dark chart and fades a light one toward white (black left
          a banded grey ring on white). The shadow, and in light mode a more
          solid surface so the ring separates from a near-white chart, follow
          the app's `.light` class like the mosaic and panel surfaces do. */}
      <style>{`[data-tool-wheel] {
          --edge-wheel-shadow: 0 0 56px -12px rgba(0, 0, 0, 0.6);
        }
        .light [data-tool-wheel] {
          --edge-wheel-shadow: 0 0 32px -10px rgba(15, 23, 42, 0.18);
        }
        .light [data-tool-wheel] [data-wheel-slice]:not([data-wheel-focus]) {
          background-color: hsl(var(--popover) / 0.84);
        }
        .light [data-tool-wheel] [data-wheel-slice][data-wheel-focus] {
          background-color: color-mix(in srgb, hsl(var(--chart-text)) 7%, hsl(var(--popover) / 0.92));
        }
        .light [data-tool-wheel] [data-wheel-hub] {
          background-color: hsl(var(--popover) / 0.9);
        }
        .edge-tool-wheel-scrim {
          background: radial-gradient(
            circle closest-side,
            hsl(var(--chart-canvas) / 0.55) 0%,
            hsl(var(--chart-canvas) / 0.42) 40%,
            hsl(var(--chart-canvas) / 0.22) 70%,
            hsl(var(--chart-canvas) / 0.08) 88%,
            hsl(var(--chart-canvas) / 0) 100%
          );
        }
        .edge-tool-wheel-fade {
          transition: opacity ${ENTER_MS}ms ease-out;
        }
        .edge-tool-wheel-slice.edge-tool-wheel-fade {
          transition: opacity ${ENTER_MS}ms ease-out, background-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
        }
        .edge-tool-wheel-pop {
          transition: transform ${ENTER_MS}ms cubic-bezier(0.2, 0.9, 0.3, 1.15);
        }
        @starting-style {
          .edge-tool-wheel-fade { opacity: 0; }
          .edge-tool-wheel-pop { transform: scale(0.94); }
        }
        [data-wheel-exiting] .edge-tool-wheel-fade {
          opacity: 0;
        }
        [data-wheel-exiting] .edge-tool-wheel-pop {
          transform: scale(0.96);
        }
        [data-wheel-exiting] .edge-tool-wheel-fade,
        [data-wheel-exiting] .edge-tool-wheel-pop {
          transition-duration: ${WHEEL_EXIT_MS}ms;
          transition-timing-function: ease-in;
        }
        @media (prefers-reduced-motion: reduce) {
          .edge-tool-wheel-fade, .edge-tool-wheel-pop { transition: none !important; }
          [data-wheel-exiting] { visibility: hidden; }
        }`}</style>
      <div
        aria-hidden="true"
        data-wheel-scrim=""
        className={`edge-tool-wheel-scrim ${FADE} pointer-events-none absolute rounded-full`}
        style={{
          left: (SIZE - SCRIM) / 2,
          top: (SIZE - SCRIM) / 2,
          width: SCRIM,
          height: SCRIM,
        }}
      />
      <div className="edge-tool-wheel-pop absolute inset-0">
        <div
          aria-hidden="true"
          className={`${FADE} pointer-events-none absolute rounded-full`}
          style={{
            left: C - ring.outer,
            top: C - ring.outer,
            width: ring.outer * 2,
            height: ring.outer * 2,
            boxShadow: 'var(--edge-wheel-shadow)',
          }}
        />

        {/* The slight glow around the slice in focus: its shape in the
            accent, blurred, UNDER the glass. Outside the slice it reads as a
            halo; the glass above softens the rest, so it spills gently onto
            the neighbouring edges instead of painting over them. */}
        {focus !== null && slices[focus] && (
          <svg
            aria-hidden="true"
            width={SIZE}
            height={SIZE}
            viewBox={`0 0 ${SIZE} ${SIZE}`}
            data-wheel-glow={String(focus)}
            className={`${FADE} pointer-events-none absolute inset-0 overflow-visible`}
          >
            <defs>
              <filter id={glowId} x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation={5} />
              </filter>
            </defs>
            <path
              d={slices[focus].d}
              filter={`url(#${glowId})`}
              fillOpacity={0.4}
              style={{ fill: 'hsl(var(--primary))' }}
            />
          </svg>
        )}

        {slices.map((slice, i) => (
          <div
            key={`slice-${i}`}
            aria-hidden="true"
            data-wheel-slice=""
            data-wheel-focus={focus === i ? String(i) : undefined}
            className={
              `edge-tool-wheel-slice ${FADE} pointer-events-none absolute inset-0 ${GLASS} ${SHEEN} ` +
              (focus === i ? SLICE_FOCUS_FILL : SLICE_FILL)
            }
            style={{ clipPath: `path("${slice.d}")` }}
          />
        ))}

        <svg
          aria-hidden="true"
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          data-wheel-edges=""
          className={`${FADE} pointer-events-none absolute inset-0 overflow-visible`}
        >
          {slices.map((slice, i) => (
            <path
              key={`edge-${i}`}
              d={slice.d}
              fill="none"
              strokeWidth={1}
              style={{
                // The dropdowns' `border-primary/15` edge, a little brighter on
                // glass so the slices keep a crisp rim; stronger on focus.
                stroke: focus === i ? 'hsl(var(--primary) / 0.55)' : 'hsl(var(--primary) / 0.18)',
              }}
            />
          ))}
        </svg>

        {/* The light catch: every slice outline again, in a faint white, the
            same all the way round so no side reads darker than another. */}
        <svg
          aria-hidden="true"
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          data-wheel-glint=""
          className={`${FADE} pointer-events-none absolute inset-0 overflow-visible`}
        >
          {slices.map((slice, i) => (
            <path
              key={`glint-${i}`}
              d={slice.d}
              fill="none"
              strokeWidth={1}
              stroke="#ffffff"
              strokeOpacity={0.14}
            />
          ))}
        </svg>

        {/* The icons fade as one layer: no glass inside it. */}
        <div aria-hidden="true" className={`${FADE} pointer-events-none absolute inset-0`}>
          {views.map((view, i) => {
            const a = slotAngle(i, n)
            const hot = focus === i
            return (
              <div
                key={i}
                className="absolute flex items-center justify-center"
                style={{
                  width: cellW,
                  height: cellH,
                  left: C + slotRadius * Math.cos(a) - cellW / 2,
                  top: C + slotRadius * Math.sin(a) - cellH / 2,
                }}
              >
                <span
                  data-wheel-icon={i}
                  className={
                    'flex h-[25px] w-[25px] shrink-0 items-center justify-center transition-colors duration-150 [&>svg]:h-[25px] [&>svg]:w-[25px] ' +
                    (hot ? 'text-primary' : 'text-[hsl(var(--chart-text)/0.84)]') +
                    (view.available ? '' : ' opacity-40')
                  }
                >
                  {view.icon}
                </span>
                {(slots[i] === activeTool || view.on) && (
                  <span
                    className="absolute h-[5px] w-[5px] rounded-full bg-primary"
                    style={{ left: cellW / 2 + 13, top: 9 }}
                  />
                )}
              </div>
            )
          })}
        </div>

        {slots.map((name, i) => {
          const a = slotAngle(i, n)
          const half = Math.PI / n
          const pt = (ang: number) => `${C + SIZE * Math.cos(ang)}px ${C + SIZE * Math.sin(ang)}px`
          const view = views[i]
          return (
            <button
              key={`wedge-${i}`}
              type="button"
              role="menuitem"
              tabIndex={-1}
              aria-label={view.label}
              aria-disabled={view.available ? undefined : true}
              title={view.label}
              data-armed={name === activeTool ? 'true' : undefined}
              data-on={view.on ? 'true' : undefined}
              className={
                'absolute inset-0 rounded-full outline-none ' +
                (editing ? 'cursor-context-menu' : 'cursor-pointer')
              }
              style={{
                clipPath: `polygon(${C}px ${C}px, ${pt(a - half)}, ${pt(a)}, ${pt(a + half)})`,
              }}
              onClick={(e) => onTileClick(i, e)}
              // Never take focus from the page: the wheel is a pointer gesture.
              onMouseDown={(e) => e.preventDefault()}
              onContextMenu={(e) => {
                e.preventDefault()
                onTileContextMenu(i, e)
              }}
            />
          )
        })}

        <div
          data-wheel-hub=""
          className={`${FADE} absolute flex flex-col items-center justify-center gap-1.5 rounded-full bg-popover/72 ${GLASS} ${SHEEN}`}
          style={{
            width: WHEEL_HUB,
            height: WHEEL_HUB,
            left: C - HUB_R,
            top: C - HUB_R,
            boxShadow: 'inset 0 0 0 1px hsl(var(--primary) / 0.18)',
          }}
        >
          {hovered ? (
            <>
              {!editing && (
                <span className="flex h-6 w-6 items-center justify-center text-primary [&>svg]:h-6 [&>svg]:w-6">
                  {hovered.icon}
                </span>
              )}
              <span
                data-hub-title=""
                className="line-clamp-2 max-w-[88px] text-center text-xs font-normal leading-tight text-[hsl(var(--chart-text))]"
              >
                {hovered.label}
              </span>
            </>
          ) : (
            <span className="max-w-[84px] text-center text-[11px] leading-tight text-[hsl(var(--chart-text-muted))]">
              {editing ? 'Click a tool to swap' : 'Move toward a tool'}
            </span>
          )}
          {editing ? (
            <div className="flex items-center gap-1">
              <HubButton
                label="Lock tool wheel"
                title="Done customizing"
                active
                onClick={onToggleEdit}
              >
                {wrenchIcon}
              </HubButton>
              <HubButton
                label="Reset tool wheel"
                title="Reset to the default tools"
                disabled={!canReset}
                onClick={onReset}
              >
                {resetIcon}
              </HubButton>
            </div>
          ) : (
            <HubButton
              label="Customize tool wheel"
              title="Customize the wheel"
              quiet
              onClick={onToggleEdit}
            >
              {wrenchIcon}
            </HubButton>
          )}
        </div>
      </div>
    </div>
  )
}
